<script lang="ts">
  // Desktop rail (spec §8, components doc §1): brand + collapse toggle, compose, special
  // folders, the custom-folder tree with collapse / "more", outbox / follow-ups / split-count
  // entries, saved searches, labels, and the new-folder / contacts / settings buttons.
  import { badgeText, t } from '$lib/i18n';
  import { displayName, folderIcon, leafName, type PathContext } from '$lib/mail/folders';
  import { readRail, store, writeRail } from '$lib/store';
  import type { FolderRec } from '$lib/types';
  import { tooltip } from '$lib/util/dom';
  import Icon from '../ui/Icon.svelte';
  import { createArchiveFlow, deleteFolderFlow, folderSections, isCustom, newFolderFlow, newSubfolderFlow, renameFolderFlow } from './_local';
  import type { FolderCtx } from './FolderMenu.svelte';
  import { folderUi } from './folderUi.svelte';

  let {
    mini = $bindable(readRail() === 'mini'),
    oncontext,
  }: {
    mini?: boolean;
    oncontext?: (ev: MouseEvent, ctx: FolderCtx) => void;
  } = $props();

  let dropHot = $state<string | null>(null);

  const sections = $derived(folderSections(store.folders, folderUi.collapsed, folderUi.moreFolders, store.currentFolder));
  const split = $derived(store.splitUnread);
  const splitAny = $derived(!!split && (split.personal > 0 || split.list > 0));
  const q = $derived(store.searchQ.trim());
  const isList = $derived(store.screen.name === 'list' || store.screen.name === 'read');

  function toggleRail() {
    mini = !mini;
    writeRail(mini ? 'mini' : 'full');
  }
  function active(path: string): boolean {
    return isList && store.currentFolder === path && !store.followupOn;
  }
  function ctxOf(f: FolderRec, ctx: PathContext): FolderCtx {
    return { path: f.path, name: displayName(f, ctx), custom: isCustom(f, ctx) };
  }
  function onCtx(ev: MouseEvent, f: FolderRec, ctx: PathContext) {
    if (!oncontext) return;
    ev.preventDefault();
    oncontext(ev, ctxOf(f, ctx));
  }
  // ---- drag-and-drop targets (spec §8)
  function onDragOver(ev: DragEvent, path: string) {
    if (!store.dragUids || path === store.dragFrom) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'move';
    dropHot = path;
  }
  function onDragLeave(path: string) {
    if (dropHot === path) dropHot = null;
  }
  function onDrop(ev: DragEvent, path: string) {
    ev.preventDefault();
    dropHot = null;
    void store.dropOn(path);
  }
</script>

<aside class="rail desktop-only" class:mini>
  <div class="brand">
    <button type="button" class="railtog" aria-expanded={!mini} aria-label={mini ? t('railExpand') : t('railCollapse')} use:tooltip onclick={toggleRail}><Icon name="list" size={20} /></button>
    <img src="/img/logo.svg" alt="" width="28" height="28" />
    <strong>{t('appName')}</strong>
  </div>
  <button type="button" class="compose-btn" aria-label={t('compose')} onclick={() => store.openCompose()}><Icon name="pen" size={17} /> <span>{t('compose')}</span></button>
  <nav aria-label={t('folders')}>
    {#each sections.special as f (f.path)}
      {@const name = displayName(f, sections.ctx)}
      <button
        type="button"
        class="fbtn"
        class:active={active(f.path)}
        class:drop-hot={dropHot === f.path}
        aria-current={active(f.path) ? 'page' : undefined}
        aria-label={name}
        onclick={() => void store.openFolder(f.path)}
        oncontextmenu={(ev) => onCtx(ev, f, sections.ctx)}
        ondragover={(ev) => onDragOver(ev, f.path)}
        ondragleave={() => onDragLeave(f.path)}
        ondrop={(ev) => onDrop(ev, f.path)}
      >
        <span class="fi"><Icon name={folderIcon(f, sections.ctx)} size={18} /></span><span class="fn">{name}</span>
        {#if f.unseen}<span class="badge">{badgeText(f.unseen)}</span>{/if}
      </button>
    {/each}
    {#if !store.archivePath}
      <button type="button" class="fbtn" aria-label={t('createArchive')} onclick={() => void createArchiveFlow()}><span class="fi"><Icon name="plus" size={18} /></span><span class="fn">{t('createArchive')}</span></button>
    {/if}
    {#if sections.custom.length}
      <div class="sheet-sep"></div>
      {#each sections.custom as n (n.folder.path)}
        {@const f = n.folder}
        {@const leaf = leafName(f.path, f.name, sections.delimiter)}
        {@const collapsed = folderUi.collapsed.has(f.path)}
        <div class="frow managed" data-depth={n.depth} data-path={f.path} style="--depth:{n.depth}">
          {#if n.hasChildren}
            <button type="button" class="twisty" aria-expanded={!collapsed} aria-label={leaf} onclick={() => folderUi.toggleCollapsed(f.path)}><Icon name="chev" size={14} /></button>
          {/if}
          <button
            type="button"
            class="fbtn nested"
            class:active={active(f.path)}
            class:drop-hot={dropHot === f.path}
            aria-current={active(f.path) ? 'page' : undefined}
            aria-label={f.path}
            onclick={() => void store.openFolder(f.path)}
            oncontextmenu={(ev) => onCtx(ev, f, sections.ctx)}
            ondragover={(ev) => onDragOver(ev, f.path)}
            ondragleave={() => onDragLeave(f.path)}
            ondrop={(ev) => onDrop(ev, f.path)}
          >
            <span class="fi"><Icon name={folderIcon(f, sections.ctx)} size={18} /></span><span class="fn">{leaf}</span>
            {#if collapsed && n.subtreeUnseen}<span class="badge roll">{badgeText(n.subtreeUnseen)}</span>{:else if f.unseen}<span class="badge">{badgeText(f.unseen)}</span>{/if}
          </button>
          <button type="button" class="mini" aria-label={t('newSubfolder')} use:tooltip onclick={() => void newSubfolderFlow(f.path, leaf)}><Icon name="plus" size={15} /></button>
          <button type="button" class="mini" aria-label={t('renameF')} use:tooltip onclick={() => void renameFolderFlow(f.path, leaf)}><Icon name="pen" size={15} /></button>
          <button type="button" class="mini danger-h" aria-label={t('delFolder')} use:tooltip onclick={() => void deleteFolderFlow(f.path, leaf)}><Icon name="trash" size={15} /></button>
        </div>
      {/each}
    {/if}
    {#if store.pendingSends.length}
      <button type="button" class="fbtn obtn" class:active={store.screen.name === 'outbox'} aria-current={store.screen.name === 'outbox' ? 'page' : undefined} onclick={() => store.openOutbox()}>
        <span class="fi"><Icon name="send" size={18} /></span><span class="fn">{t('outbox')}</span><span class="badge">{badgeText(store.pendingSends.length)}</span>
      </button>
    {/if}
    {#if store.followupCount}
      <button type="button" class="fbtn fubtn" class:active={store.followupOn} onclick={() => void store.showFollowups()}>
        <span class="fi"><Icon name="refresh" size={18} /></span><span class="fn">{t('followup')}</span><span class="badge">{badgeText(store.followupCount)}</span>
      </button>
    {/if}
    {#if splitAny && split}
      <div class="sheet-sep"></div>
      <button type="button" class="fbtn spbtn" class:active={q === 'is:personal'} data-split="personal" aria-label={t('op_personal')} onclick={() => store.runSearch('is:personal')}>
        <span class="fi"><Icon name="users" size={18} /></span><span class="fn">{t('op_personal')}</span>{#if split.personal}<span class="badge">{badgeText(split.personal)}</span>{/if}
      </button>
      <button type="button" class="fbtn spbtn" class:active={q === 'is:list'} data-split="list" aria-label={t('op_list')} onclick={() => store.runSearch('is:list')}>
        <span class="fi"><Icon name="sort" size={18} /></span><span class="fn">{t('op_list')}</span>{#if split.list}<span class="badge">{badgeText(split.list)}</span>{/if}
      </button>
    {/if}
    {#if sections.showMore}
      <button type="button" class="fbtn morebtn" aria-label={folderUi.moreFolders ? t('showLess') : t('showMoreN', { n: sections.hidden })} onclick={() => folderUi.setMore(!folderUi.moreFolders)}>
        <span class="fi"><Icon name="more" size={18} /></span><span class="fn">{folderUi.moreFolders ? t('showLess') : t('showMoreN', { n: sections.hidden })}</span>
      </button>
    {/if}
    {#if store.savedSearches.length}
      <div class="sheet-sep"></div>
      <div class="rail-h">{t('savedSearches')}</div>
      {#each store.savedSearches as s (s.id)}
        <div class="frow managed">
          <button type="button" class="fbtn lbtn" class:active={q === s.q} aria-current={q === s.q ? 'page' : undefined} aria-label={s.q} onclick={() => store.runSearch(s.q)}>
            <span class="fi"><Icon name="sort" size={16} /></span><span class="fn"><bdi>{s.name}</bdi></span>
          </button>
          <button type="button" class="mini danger-h" aria-label={t('del')} use:tooltip onclick={() => void store.removeSearch(s.id)}><Icon name="trash" size={15} /></button>
        </div>
      {/each}
    {/if}
    {#if store.knownLabels.length}
      <div class="sheet-sep"></div>
      <div class="rail-h">{t('labels')}</div>
      {#each store.knownLabels as l (l)}
        <button type="button" class="fbtn lbtn" class:active={store.searchLabel === l} aria-current={store.searchLabel === l ? 'page' : undefined} aria-label={l} onclick={() => store.filterByLabel(l)}>
          <span class="fi"><Icon name="star" size={16} /></span><span class="fn"><bdi>{l}</bdi></span>{#if store.labelCounts[l]}<span class="lcount">{store.labelCounts[l]}</span>{/if}
        </button>
      {/each}
    {/if}
  </nav>
  <button type="button" class="fbtn" aria-label={t('newFolder')} onclick={() => void newFolderFlow()}><span class="fi"><Icon name="plus" size={18} /></span><span class="fn">{t('newFolder')}</span></button>
  <button type="button" class="fbtn" class:active={store.screen.name === 'contacts'} aria-label={t('contacts')} onclick={() => store.openContacts()}><span class="fi"><Icon name="users" size={18} /></span><span class="fn">{t('contacts')}</span></button>
  <button type="button" class="fbtn" class:active={store.screen.name === 'settings'} aria-label={t('settings')} onclick={() => store.openSettings()}><span class="fi"><Icon name="gear" size={18} /></span><span class="fn">{t('settings')}</span></button>
  {#if store.user}
    <div class="rail-user">
      <span class="fn" title={store.user}><bdi dir="ltr">{store.user}</bdi></span>
      <button type="button" class="ghost" aria-label={t('logout')} use:tooltip onclick={() => void store.logout()}><Icon name="logout" size={16} /></button>
    </div>
  {/if}
</aside>

<style>
  /* @css shell/Rail start */
  .frow[data-depth] .fbtn.nested {
    padding-inline-start: calc(var(--space-3) + var(--depth) * var(--space-4))
  }

  .fbtn.drop-hot {
    background: var(--surface-hover);
    box-shadow: inset 0 0 0 2px var(--blue-500)
  }

  .twisty {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--control-xs);
    height: var(--control-xs);
    border: 0;
    background: none;
    cursor: pointer;
    color: var(--text-faint);
    flex: 0 0 auto
  }

  .twisty:hover {
    color: var(--text-body)
  }

  .twisty[aria-expanded=false] :global(svg) {
    transform: rotate(-90deg)
  }

  :global([dir=rtl]) .twisty[aria-expanded=false] :global(svg) {
    transform: rotate(90deg)
  }

  .badge.roll {
    opacity: .85
  }

  .rail-h {
    padding: var(--space-2) var(--space-4) 0;
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-weight: var(--weight-medium)
  }

  .lbtn .fn {
    font-weight: var(--weight-regular)
  }

  .lcount {
    margin-inline-start: auto;
    color: var(--text-muted);
    font-size: var(--text-xs);
    font-variant-numeric: tabular-nums
  }

  .rail {
    background: var(--surface-subtle);
    border-inline-end: 1px solid var(--border-subtle);
    display: flex;
    flex-direction: column;
    padding: var(--space-4) var(--space-2);
    gap: var(--space-3);
    min-height: 0;
    overflow-y: auto
  }

  .brand {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding-inline: var(--space-3);
    color: var(--text-strong)
  }

  .railtog {
    display: grid;
    place-items: center;
    width: var(--control-md);
    height: var(--control-md);
    flex: none;
    margin-inline-start: calc(-1 * var(--space-2));
    border-radius: var(--radius-md);
    color: var(--text-muted)
  }

  .railtog:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .rail.mini {
    padding-inline: var(--space-2)
  }

  .rail.mini .brand {
    padding-inline: 0;
    justify-content: center
  }

  .rail.mini .brand img,
  .rail.mini .brand strong {
    display: none
  }

  .rail.mini .railtog {
    margin-inline-start: 0
  }

  .rail.mini .compose-btn {
    margin-inline: 0;
    padding: 0
  }

  .rail.mini .compose-btn span {
    display: none
  }

  .rail.mini .fbtn {
    justify-content: center;
    padding-inline: 0;
    position: relative
  }

  .rail.mini .fn,
  .rail.mini .lcount,
  .rail.mini .rail-h,
  .rail.mini .twisty,
  .rail.mini .frow.managed .mini {
    display: none
  }

  .rail.mini .fbtn.nested,
  .rail.mini .frow[data-depth] .fbtn.nested {
    padding-inline-start: 0
  }

  .rail.mini .fi {
    width: auto
  }

  .rail.mini .badge {
    position: absolute;
    top: 2px;
    inset-inline-end: 6px;
    padding: 0 4px;
    font-size: var(--text-eyebrow);
    line-height: 1.4;
    pointer-events: none
  }

  .compose-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    background: var(--blue-500);
    color: var(--text-on-accent);
    min-block-size: var(--control-lg);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium);
    box-shadow: var(--shadow-sm);
    margin-inline: var(--space-1);
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .compose-btn:hover {
    background: var(--blue-600);
    box-shadow: var(--shadow-md)
  }

  .compose-btn:active {
    transform: scale(.99)
  }

  nav:not(.tabbar) {
    flex: 1;
    display: grid;
    gap: var(--space-1);
    align-content: start
  }

  .fbtn {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding-block: var(--space-3);
    padding-inline: var(--space-3);
    min-block-size: var(--control-lg);
    border-radius: var(--radius-control);
    color: var(--text-body);
    width: 100%;
    text-align: start;
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .fbtn:hover {
    background: var(--surface-sunken)
  }

  .fbtn.active {
    background: var(--blue-50);
    color: var(--blue-700);
    font-weight: var(--weight-semibold)
  }

  :global(html.tik-dark) .fbtn.active {
    background: #4a7cf62e;
    color: var(--blue-200)
  }

  .frow {
    display: flex;
    align-items: center;
    gap: var(--space-1)
  }

  .frow .fbtn {
    flex: 1;
    min-width: 0
  }

  .mini {
    display: inline-grid;
    place-items: center;
    min-inline-size: var(--control-sm);
    min-block-size: var(--control-sm);
    padding: var(--space-2);
    border-radius: var(--radius-xs);
    color: var(--text-faint);
    flex: none
  }

  .mini:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .mini.danger-h:hover {
    color: var(--danger-500)
  }

  @media (hover:hover) and (pointer:fine) {
  .frow.managed .mini {
      opacity: 0
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .frow.managed:hover .mini,
  .frow.managed:focus-within .mini {
      opacity: 1
    }
  }

  .sheet-sep {
    height: 1px;
    background: var(--border-subtle);
    margin: var(--space-2) var(--space-3)
  }

  .fi {
    width: var(--control-xs);
    display: grid;
    place-items: center
  }

  .fn {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .badge {
    background: var(--blue-500);
    color: var(--text-on-accent);
    border-radius: var(--radius-pill);
    display: grid;
    place-items: center;
    block-size: 20px;
    padding: 0 var(--space-2);
    font-size: var(--text-eyebrow);
    font-variant-numeric: tabular-nums;
    flex: none
  }

  .ghost {
    padding: var(--space-2);
    border-radius: var(--radius-control);
    color: var(--text-muted)
  }

  .ghost:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .danger-h:hover {
    color: var(--danger-500)
  }

  .fbtn:active,
  .twisty:active,
  .mini:active,
  .railtog:active,
  .ghost:active {
    background: var(--mail-pressed)
  }

  @media (max-width:900px) {
  .desktop-only {
      display: none
    }
  }

  @media (pointer:fine) {
  .rail {
      scrollbar-gutter: stable
    }
  }

  .rail nav:not(.tabbar) {
    grid-template-columns: minmax(0, 1fr)
  }

  .rail nav:not(.tabbar) > * {
    min-width: 0
  }

  .rail .fbtn,
  .rail .frow {
    min-width: 0
  }

  .rail .badge {
    margin-inline-start: var(--space-2)
  }

  @media (hover:hover) and (pointer:fine) {
  .frow.managed .mini {
      inline-size: 0;
      min-inline-size: 0;
      padding-inline: 0;
      overflow: hidden;
      opacity: 0
    }
  }

  @media (hover:hover) and (pointer:fine) {
  .frow.managed:hover .mini,
  .frow.managed:focus-within .mini {
      inline-size: auto;
      min-inline-size: var(--control-sm);
      padding-inline: var(--space-2);
      opacity: 1
    }
  }
  /* @css shell/Rail end */

  /* user footer (new in the rewrite; the legacy rail had no footer) */
  .rail-user {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding-inline: var(--space-3);
    color: var(--text-muted);
    font-size: var(--text-xs);
    min-width: 0;
  }
  .rail.mini .rail-user .fn {
    display: none;
  }
</style>
